// src/features/jobs/utils/jobToGanttFromPhasesForGant.js

const DEFAULT_START = '2019-08-01 00:00';
const DAY_MS = 24 * 60 * 60 * 1000;

// "2026-01-22T08:05:13.426Z" -> "YYYY-MM-DD HH:mm"
function fmtGanttDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function safeDate(d) {
  const x = d ? new Date(d) : null;
  return x && !isNaN(x.getTime())
    ? x
    : new Date(DEFAULT_START.replace(' ', 'T') + ':00Z');
}

function humanizePhaseKey(key) {
  if (!key) return '';
  return key
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Short phase labels for the narrow Gantt grid column (full names overflow).
const GANTT_PHASE_SHORT = {
  unwinding: 'Unwind',
  pre_stranding: 'Pre-Str',
  conductor_isolation: 'Isol',
  rope: 'Rope',
  interior_sheat: 'Inner S',
  exterior_sheat: 'Outer S',
  armouring: 'Arm',
  coiling: 'Coil',
  rewinding: 'Rewind'
};
const shortPhaseLabel = key => GANTT_PHASE_SHORT[key] || humanizePhaseKey(key);

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

// duration “intamplator”, dar stabil pe fază (să nu se schimbe la fiecare render)
function pseudoDurationDays(phaseKey, idx) {
  // 3..10 zile (doar demo)
  let seed = 0;
  for (let i = 0; i < (phaseKey || '').length; i++)
    seed += phaseKey.charCodeAt(i);
  const v = (seed + idx * 7) % 8; // 0..7
  return 3 + v; // 3..10
}

export function jobToGanttFromPhasesForGant(job) {
  if (!job) return { data: [], links: [] };

  const order = Array.isArray(job.phasesForGant) ? job.phasesForGant : [];
  const phases = job.phases || {};

  // dacă nu ai phasesForGant, măcar să nu crape
  const phaseKeys = order.length ? order : Object.keys(phases);

  const base = safeDate(job.startDate || job.createdAt);

  // Tandem: a fused task carries tandemPhases; show the combined label on the
  // surviving phase's bar (e.g. exterior_sheat → "Interior Sheat + Exterior Sheat").
  // Stacked short labels (one per line) on the surviving phase's row.
  const tandemLabelByPhase = {};
  for (const t of Object.values(job.tasks || {})) {
    if (t?.tandem && Array.isArray(t.tandemPhases) && t.tandemPhases.length) {
      tandemLabelByPhase[t.phase] = t.tandemPhases.map(shortPhaseLabel).join('\n');
    }
  }

  // phaseKey -> ganttId
  const idByPhase = new Map();
  phaseKeys.forEach((k, i) => idByPhase.set(k, i + 1));

  const data = phaseKeys.map((phaseKey, idx) => {
    const phaseInfo = phases[phaseKey] || {};
    // Lua stores progress as 0-100 integer; Gantt expects 0-1 float
    const rawProgress = Number(phaseInfo.progress) || 0;
    const progress = rawProgress > 1 ? clamp01(rawProgress / 100) : clamp01(rawProgress);

    // simulăm o dată diferită pe fază (1 zi distanță)
    const start = new Date(base.getTime() + idx * DAY_MS);

    return {
      id: idx + 1,
      text: tandemLabelByPhase[phaseKey] || shortPhaseLabel(phaseKey),
      start_date: fmtGanttDate(start),
      duration: pseudoDurationDays(phaseKey, idx), // “număr întâmplător”
      progress, // din job.phases[phaseKey].progress
      task_class: phaseKey
    };
  });

  // links (FS) din dependsOn, dar respectând id-urile din phasesForGant
  const links = [];
  let linkId = 1;

  for (const phaseKey of phaseKeys) {
    const dep = phases?.[phaseKey]?.dependsOn;
    if (!dep) continue;
    if (!idByPhase.has(dep) || !idByPhase.has(phaseKey)) continue;

    links.push({
      id: linkId++,
      source: idByPhase.get(dep),
      target: idByPhase.get(phaseKey),
      type: '0'
    });
  }

  return { data, links };
}
